import React from "react";
import { useLanguage } from "@/App";
import { 
  Plus, 
  Globe, 
  BarChart3, 
  Share2, 
  Mail, 
  Sparkles, 
  FileText, 
  Camera, 
  Shield, 
  Headphones,
  ArrowRight,
  Zap
} from "lucide-react";
import { motion } from "framer-motion";

const AddOns = () => {
  const { language } = useLanguage();

  const addons = [
    {
      icon: FileText,
      title_en: "Extra Pages",
      title_nl: "Extra Pagina's",
      description_en: "Additional pages beyond your package",
      description_nl: "Extra pagina's bovenop je pakket",
      price: "€149",
      unit_en: "/page",
      unit_nl: "/pagina"
    },
    {
      icon: Globe,
      title_en: "Multi-Language",
      title_nl: "Meertaligheid",
      description_en: "Add extra language versions to your site",
      description_nl: "Voeg extra taalversies toe aan je site",
      price: "€399",
      unit_en: "/language",
      unit_nl: "/taal"
    },
    {
      icon: BarChart3,
      title_en: "Analytics Setup",
      title_nl: "Analytics Setup",
      description_en: "Google Analytics 4 + custom dashboard",
      description_nl: "Google Analytics 4 + custom dashboard",
      price: "€249",
      unit_en: "",
      unit_nl: ""
    },
    {
      icon: Share2,
      title_en: "Social Media Integration",
      title_nl: "Social Media Integratie",
      description_en: "Connect feeds, sharing & login",
      description_nl: "Verbind feeds, delen & inloggen",
      price: "€199",
      unit_en: "",
      unit_nl: ""
    },
    {
      icon: Mail,
      title_en: "Email Marketing Setup",
      title_nl: "E-mail Marketing Setup",
      description_en: "Mailchimp/SendGrid with templates",
      description_nl: "Mailchimp/SendGrid met templates",
      price: "€349",
      unit_en: "",
      unit_nl: ""
    },
    {
      icon: Sparkles,
      title_en: "Custom Animations",
      title_nl: "Custom Animaties",
      description_en: "Premium micro-interactions & effects",
      description_nl: "Premium micro-interacties & effecten",
      price: "€499",
      unit_en: "",
      unit_nl: ""
    },
    {
      icon: Camera,
      title_en: "Professional Photography",
      title_nl: "Professionele Fotografie",
      description_en: "On-site photo & video shoot",
      description_nl: "On-locatie foto & video shoot",
      price: "€699",
      unit_en: "",
      unit_nl: ""
    },
    {
      icon: Shield,
      title_en: "Advanced Security",
      title_nl: "Geavanceerde Beveiliging",
      description_en: "WAF, DDoS protection & hardening",
      description_nl: "WAF, DDoS bescherming & hardening",
      price: "€199",
      unit_en: "/month",
      unit_nl: "/maand"
    },
    {
      icon: Headphones,
      title_en: "Priority Support",
      title_nl: "Prioriteit Support",
      description_en: "Dedicated support with 1h response",
      description_nl: "Dedicated support met 1u reactie",
      price: "€149",
      unit_en: "/month",
      unit_nl: "/maand"
    },
    {
      icon: Zap,
      title_en: "Speed Optimization",
      title_nl: "Snelheid Optimalisatie",
      description_en: "Core Web Vitals & performance boost",
      description_nl: "Core Web Vitals & prestatie boost",
      price: "€349",
      unit_en: "",
      unit_nl: ""
    }
  ];

  const getTitle = (a) => language === "nl" ? a.title_nl : a.title_en;
  const getDesc = (a) => language === "nl" ? a.description_nl : a.description_en;
  const getUnit = (a) => language === "nl" ? a.unit_nl : a.unit_en;

  return (
    <section
      id="addons"
      data-testid="addons-section"
      className="py-32 bg-[#121212] relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FF6B00]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FF6B00]/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 bg-[#FF6B00]/10 border border-[#FF6B00]/20 px-5 py-2 mb-6">
            <Plus size={16} className="text-[#FF6B00]" />
            <span className="text-[#FF6B00] text-xs font-bold uppercase tracking-[0.2em]">
              {language === "nl" ? "Upgrade Je Pakket" : "Upgrade Your Package"}
            </span>
          </span>
          <h2
            data-testid="addons-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {language === "nl" ? "Krachtige " : "Powerful "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF4500]">
              Add-Ons
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {language === "nl"
              ? "Maak je pakket compleet met deze krachtige toevoegingen. Mix & match naar behoefte."
              : "Complete your package with these powerful additions. Mix & match as needed."}
          </p>
        </motion.div>

        {/* Add-ons Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {addons.map((addon, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.03 }}
              data-testid={`addon-card-${index}`}
              className="group bg-[#0A0A0A] border border-white/5 p-5 hover:border-[#FF6B00]/30 hover:bg-[#0f0f0f] transition-all cursor-pointer"
            >
              {/* Icon */}
              <div className="w-10 h-10 bg-[#FF6B00]/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <addon.icon size={20} className="text-[#FF6B00]" />
              </div>

              {/* Title */}
              <h3 className="text-white font-bold text-sm mb-1">
                {getTitle(addon)}
              </h3>

              {/* Description */}
              <p className="text-zinc-500 text-xs leading-relaxed mb-3">
                {getDesc(addon)}
              </p>

              {/* Price */}
              <div className="mt-auto">
                <span className="text-[#FF6B00] font-black text-lg">{addon.price}</span>
                <span className="text-zinc-500 text-xs">{getUnit(addon)}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B00]/10 to-purple-500/10 blur-xl" />
            <div className="relative bg-[#0A0A0A] border border-white/10 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {language === "nl" ? "Alles combineren? Vraag om bundel korting!" : "Combining everything? Ask for bundle discount!"}
                </h3>
                <p className="text-zinc-400 text-sm">
                  {language === "nl"
                    ? "Neem meerdere add-ons af en ontvang tot 20% korting op het totaalpakket."
                    : "Take multiple add-ons and receive up to 20% discount on the total package."}
                </p>
              </div>
              <a
                href="#contact"
                data-testid="addons-bundle-cta"
                className="flex-shrink-0 group inline-flex items-center gap-3 bg-gradient-to-r from-[#FF6B00] to-[#FF4500] text-white px-8 py-4 font-bold hover:shadow-[0_0_30px_rgba(255,107,0,0.4)] transition-all"
              >
                {language === "nl" ? "Offerte Aanvragen" : "Get A Quote"}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AddOns;
